import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import * as OneSignalService from '@/lib/oneSignalService';

export type NotificationPermissionState = 'default' | 'granted' | 'denied';

interface UseNotificationsReturn {
  // Status
  isSupported: boolean;
  isEnabled: boolean;
  permission: NotificationPermissionState;
  isIOS: boolean;
  isInstalledPWA: boolean;
  needsIOSInstall: boolean;
  isDenied: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  hasDbSubscription: boolean;
  isDesynchronized: boolean;
  
  // Actions
  enable: () => Promise<boolean>;
  disable: () => Promise<boolean>;
  toggle: (enabled: boolean) => Promise<boolean>;
  sendTestNotification: () => Promise<boolean>;
  refresh: () => Promise<void>;
  repair: () => Promise<boolean>;
}

export function useNotifications(): UseNotificationsReturn {
  const { user, profile, updateProfile } = useAuth();
  const [status, setStatus] = useState<OneSignalService.OneSignalNotificationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasDbSubscription, setHasDbSubscription] = useState(false);
  const desyncCheckDone = useRef(false);

  // Load status with protection against concurrent calls
  const loadStatus = useCallback(async () => {
    try {
      const newStatus = await OneSignalService.getStatus();
      setStatus(newStatus);
      
      // Check DB subscription if user exists
      if (user) {
        const hasSub = await OneSignalService.hasDbSubscription(user.id);
        setHasDbSubscription(hasSub);
      }
    } catch (error) {
      console.error('[useNotifications] Status load error:', error);
    }
    setIsLoading(false);
  }, [user]);

  // Initialize OneSignal and load status - runs only once
  useEffect(() => {
    if (hasInitialized) return;
    
    let isMounted = true;
    
    const init = async () => {
      console.log('[useNotifications] Starting initialization...');
      const startTime = Date.now();
      
      try {
        await OneSignalService.initOneSignal();
        
        // Login user if authenticated
        if (user && isMounted) {
          await OneSignalService.loginUser(user.id);
        }
        
        if (isMounted) {
          await loadStatus();
          const duration = Date.now() - startTime;
          console.log(`[useNotifications] Initialization complete in ${duration}ms`);
        }
      } catch (error) {
        console.error('[useNotifications] Init error:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    setHasInitialized(true);
    init();
    
    return () => {
      isMounted = false;
    };
  }, [user, loadStatus, hasInitialized]);

  // Logout from OneSignal when user logs out
  useEffect(() => {
    if (!user && status?.isInitialized) {
      OneSignalService.logoutUser();
    }
  }, [user, status?.isInitialized]);

  // Check for desynchronization (profile says enabled but no DB subscription)
  useEffect(() => {
    if (!user || isLoading || desyncCheckDone.current) return;
    
    const profileEnabled = profile?.push_notifications_enabled === true;
    const isDesync = profileEnabled && !hasDbSubscription;
    
    if (isDesync) {
      desyncCheckDone.current = true;
      console.warn('[useNotifications] Desync detected: profile enabled but no DB subscription');
      
      // Auto-correct the profile flag
      updateProfile({ push_notifications_enabled: false }).then(() => {
        toast.warning('Notifications désynchronisées', {
          description: 'Veuillez réactiver les notifications.',
          duration: 5000,
        });
      });
    }
  }, [user, profile, hasDbSubscription, isLoading, updateProfile]);

  // Enable notifications
  const enable = useCallback(async (): Promise<boolean> => {
    if (!user || isActionInProgress) return false;
    
    console.log('[useNotifications] Starting enable flow...');
    setIsActionInProgress(true);
    setIsLoading(true);

    try {
      // Check iOS requirements
      if (status?.isIOS && !status?.isInstalledPWA) {
        console.warn('[useNotifications] iOS requires PWA installation');
        return false;
      }

      const result = await OneSignalService.subscribe(user.id);
      console.log('[useNotifications] Subscribe result:', result);
      
      if (result.success) {
        await updateProfile({ push_notifications_enabled: true });
        console.log('[useNotifications] Profile updated');
      }

      await loadStatus();
      return result.success;
    } catch (error) {
      console.error('[useNotifications] Enable error:', error);
      await loadStatus();
      return false;
    } finally {
      setIsActionInProgress(false);
      setIsLoading(false);
    }
  }, [user, status, updateProfile, loadStatus, isActionInProgress]);

  // Disable notifications
  const disable = useCallback(async (): Promise<boolean> => {
    if (!user || isActionInProgress) return false;
    
    console.log('[useNotifications] Starting disable flow...');
    setIsActionInProgress(true);
    setIsLoading(true);

    try {
      const success = await OneSignalService.unsubscribe(user.id);
      console.log('[useNotifications] Unsubscribe result:', success);
      
      if (success) {
        await updateProfile({ push_notifications_enabled: false });
      }

      await loadStatus();
      return success;
    } catch (error) {
      console.error('[useNotifications] Disable error:', error);
      await loadStatus();
      return false;
    } finally {
      setIsActionInProgress(false);
      setIsLoading(false);
    }
  }, [user, updateProfile, loadStatus, isActionInProgress]);

  // Toggle
  const toggle = useCallback(async (enabled: boolean): Promise<boolean> => {
    return enabled ? enable() : disable();
  }, [enable, disable]);

  // Send test notification
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    const result = await OneSignalService.sendNotification(
      user.id,
      '🔔 Test',
      'Les notifications fonctionnent correctement !'
    );

    return result.success;
  }, [user]);

  // Refresh status
  const refresh = useCallback(async () => {
    setIsLoading(true);
    desyncCheckDone.current = false;
    await loadStatus();
  }, [loadStatus]);

  // Repair desynchronized subscription
  const repair = useCallback(async (): Promise<boolean> => {
    if (!user || isActionInProgress) return false;
    
    console.log('[useNotifications] Starting repair...');
    setIsActionInProgress(true);
    setIsLoading(true);

    try {
      const result = await OneSignalService.repairSubscription(user.id);
      console.log('[useNotifications] Repair result:', result);
      
      desyncCheckDone.current = true; // Prevent immediate re-check
      await loadStatus();
      return result.success;
    } catch (error) {
      console.error('[useNotifications] Repair error:', error);
      await loadStatus();
      return false;
    } finally {
      setIsActionInProgress(false);
      setIsLoading(false);
    }
  }, [user, loadStatus, isActionInProgress]);

  // Derived states
  const isSupported = status?.isSupported ?? false;
  const isEnabled = status?.isSubscribed ?? (profile?.push_notifications_enabled ?? false);
  const permission = (status?.permission ?? 'default') as NotificationPermissionState;
  const isIOS = status?.isIOS ?? false;
  const isInstalledPWA = status?.isInstalledPWA ?? false;
  const needsIOSInstall = isIOS && !isInstalledPWA;
  const isDenied = permission === 'denied';
  const isInitialized = status?.isInitialized ?? false;
  const profileEnabled = profile?.push_notifications_enabled === true;
  const isDesynchronized = profileEnabled && !hasDbSubscription;

  return {
    isSupported,
    isEnabled,
    permission,
    isIOS,
    isInstalledPWA,
    needsIOSInstall,
    isDenied,
    isLoading,
    isInitialized,
    hasDbSubscription,
    isDesynchronized,
    enable,
    disable,
    toggle,
    sendTestNotification,
    refresh,
    repair,
  };
}
